/**
 * forest.ts — BETA 23.41 · G1 FOREST WALLS (the GROUNDED WORLD, phase 1)
 *
 * Dylan's mockups inverted the map: the world is mostly IMPASSABLE lush
 * vegetation, and the walkable space is CARVED out of it — trails, plazas,
 * clearings, pockets. "This forces us to place things very specifically.
 * Forces the player to make hard decisions."
 *
 * THE FIELD, not props: a point is walkable iff it sits inside any carved
 * region — near a trail (incl. the new POI spurs), inside a set-piece plaza,
 * near a machine/shrine, near the spawn/portal/extraction anchors, or inside
 * a seeded open pocket (the roam areas). The carve radius wobbles with
 * seeded value-noise so edges are organic, never tubes. Collision is one
 * O(1)-ish query in playerFeetBlocked — no per-tree colliders, ever.
 *
 * RENDERING (mapChunks + worldProps consume this):
 *  - interior: canopy crown stamps baked into the chunk ground cache (dense
 *    dark mass, like the mockups)
 *  - boundary: real y-sorted tree_lush props (the "hem") placed just inside
 *    the blocked side so the player walks behind foliage at the edge
 *
 * Openness dial: pockets + generous corridors for G1. Per-biome density
 * arrives with G2 (biome layer).
 */

import { cellRng, getGroundPois } from './mapChunks';
import { distToNearestPath, distToNearestPark, PARK_PLAZA_R } from './paths';
import { biomeCarveMulAt } from './biomes';   // G2 — pure fn of (x,y,seed), cycle-safe

/** G1 kill-switch (mapChunks re-exports pattern kept local to avoid cycles) */
export const MAP_FOREST = true;

// ── carve radii (wu) ─────────────────────────────────────────────────────────
const TRAIL_CARVE_MIN = 165;    // corridor half-width at noise=0 (path is 81 wide — room to fight)
const TRAIL_CARVE_VAR = 150;    // + up to this much where noise opens up
const PARK_SKIRT_MIN = 130;     // beyond the plaza radius
const PARK_SKIRT_VAR = 90;
const POI_CARVE_MIN = 250;      // machines/shrines breathing room
const POI_CARVE_VAR = 70;
const ANCHOR_CARVE = 760;       // spawn / portal / extraction clearings

// ── open pockets (the roam areas) ────────────────────────────────────────────
const POCKET_CELL_WU = 3072;
const POCKET_CHANCE = 0.38;
const POCKET_RX_MIN = 950;
const POCKET_RX_VAR = 650;

// ── seeded value noise (256wu lattice, smoothstep bilinear) ──────────────────
const NOISE_CELL = 256;
function _h(a: number, b: number, seed: number): number {
  let h = (Math.imul(a | 0, 0x9E3779B1) ^ Math.imul(b | 0, 0x85EBCA6B) ^ Math.imul(seed | 0, 0xC2B2AE35)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0x27D4EB2F) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 0xFFFFFFFF;
}
function _smooth(t: number): number { return t * t * (3 - 2 * t); }
export function forestNoise(x: number, y: number, seed: number): number {
  const gx = Math.floor(x / NOISE_CELL), gy = Math.floor(y / NOISE_CELL);
  const fx = _smooth(x / NOISE_CELL - gx), fy = _smooth(y / NOISE_CELL - gy);
  const a = _h(gx, gy, seed), b = _h(gx + 1, gy, seed);
  const c = _h(gx, gy + 1, seed), d = _h(gx + 1, gy + 1, seed);
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}

// ── spawn/portal/extraction anchors (engine registers at mapgen) ─────────────
interface Anchor { x: number; y: number; r?: number }
let _anchors: Anchor[] = [];
let _onAnchorsChanged: (() => void) | null = null;
export function setForestAnchors(anchors: Anchor[]): void {
  _anchors = anchors;
  if (_onAnchorsChanged) _onAnchorsChanged();
}
/** One-off additions (extraction port spawns mid-run) */
export function addForestAnchor(a: Anchor): void {
  _anchors.push(a);
  if (_onAnchorsChanged) _onAnchorsChanged();
}
/** mapChunks hooks this so baked-canopy chunks rebuild when anchors move */
export function onForestAnchorsChanged(cb: () => void): void { _onAnchorsChanged = cb; }

// ── open pockets — ONE generator (23.57): forestClearance AND the places
// system (paths.ts) read pocket geometry from here, so a clearing and its
// trail/furnishings can never disagree (the shared-generator law).
export interface ForestPocket { x: number; y: number; rx: number; ry: number }
function _pocketForCell(cx: number, cy: number, seed: number): ForestPocket | null {
  const rng = cellRng(cx, cy, seed, 7117);
  if (rng() > POCKET_CHANCE) return null;
  const x = (cx + 0.25 + rng() * 0.5) * POCKET_CELL_WU;
  const y = (cy + 0.25 + rng() * 0.5) * POCKET_CELL_WU;
  const rx = POCKET_RX_MIN + rng() * POCKET_RX_VAR;
  return { x, y, rx, ry: rx * 0.55 };
}
/** All pockets whose center falls in the world rect (places system, 23.57). */
export function forestPocketsInRange(x0: number, y0: number, x1: number, y1: number, seed: number): ForestPocket[] {
  const out: ForestPocket[] = [];
  const c0x = Math.floor(x0 / POCKET_CELL_WU), c1x = Math.floor(x1 / POCKET_CELL_WU);
  const c0y = Math.floor(y0 / POCKET_CELL_WU), c1y = Math.floor(y1 / POCKET_CELL_WU);
  for (let cy = c0y; cy <= c1y; cy++) for (let cx = c0x; cx <= c1x; cx++) {
    const p = _pocketForCell(cx, cy, seed);
    if (p) out.push(p);
  }
  return out;
}

/**
 * How far inside WALKABLE space (x,y) is, in wu. > 0 = walkable,
 * <= 0 = forest-blocked (magnitude ≈ depth into the woods, capped).
 */
export function forestClearance(x: number, y: number, seed: number): number {
  const n = forestNoise(x, y, seed);
  let best = -1e9;

  // G2 — biome carve dial: forest biome tightens corridors, dead lot opens
  // them. Applies to TRAILS ONLY (Mariah gate correction 2026-07-06):
  // pockets are exempt because PLACES ARE POCKETS — the 23.57 place-furnish
  // pipeline bakes dressing to the pocket's stored rx/ry (groundKits 0.92*rx),
  // so resizing pockets by biome moved the treeline onto junkyard wrecks and
  // grave decals. Set-piece clearings (parks, POIs, anchors) were already
  // exempt for the same furnish-radius reason. Clamped [0.85, 1.25] in
  // biomes.ts: no trail can pinch shut. Collision and visuals both read this
  // one field, so corridors tighten/open together — one source of truth.
  const bMul = biomeCarveMulAt(x, y, seed);

  // trails (incl. POI spurs — paths.ts owns them)
  const dPath = distToNearestPath(x, y, seed);
  best = Math.max(best, (TRAIL_CARVE_MIN + TRAIL_CARVE_VAR * n) * bMul - dPath);
  if (best > 0) return best;

  // set-piece plazas
  const dPark = distToNearestPark(x, y, seed);
  best = Math.max(best, (PARK_PLAZA_R + PARK_SKIRT_MIN + PARK_SKIRT_VAR * n) - dPark);
  if (best > 0) return best;

  // machines / shrines — F7 perf (co-op audit): a POI can only improve `best`
  // when d < R - best, i.e. dSq < (R - best)². Squared compare culls the sqrt
  // for almost every POI (exact same result, no quantization).
  const poiR = POI_CARVE_MIN + POI_CARVE_VAR * n;
  for (const p of getGroundPois()) {
    const useful = poiR - best;
    if (useful <= 0) break;                       // nothing can improve best anymore
    const dx2 = x - p.x, dy2 = y - p.y;
    const dSq = dx2 * dx2 + dy2 * dy2;
    if (dSq >= useful * useful) continue;
    best = poiR - Math.sqrt(dSq);
    if (best > 0) return best;
  }

  // spawn / portal / extraction — same exact-cull
  for (const a of _anchors) {
    const aR = a.r ?? ANCHOR_CARVE;
    const useful = aR - best;
    if (useful <= 0) continue;                     // per-anchor radii differ — no break
    const dx2 = x - a.x, dy2 = y - a.y;
    const dSq = dx2 * dx2 + dy2 * dy2;
    if (dSq >= useful * useful) continue;
    best = aR - Math.sqrt(dSq);
    if (best > 0) return best;
  }

  // open pockets — seeded roam areas (ellipse, squashed like every ground shape)
  const pcx = Math.floor(x / POCKET_CELL_WU), pcy = Math.floor(y / POCKET_CELL_WU);
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const pk = _pocketForCell(pcx + dx, pcy + dy, seed);
    if (!pk) continue;
    // elliptical distance, in "rx units" → convert to wu-ish clearance.
    // NO biome dial here — places furnish to the stored rx/ry (see above).
    const ex = (x - pk.x) / pk.rx, ey = (y - pk.y) / pk.ry;
    const e = Math.sqrt(ex * ex + ey * ey);
    best = Math.max(best, (1 - e) * pk.rx * 0.8);
    if (best > 0) return best;
  }

  return best;
}

export function isForestBlockedAt(x: number, y: number, seed: number): boolean {
  return forestClearance(x, y, seed) <= 0;
}

/** 23.50 — MOVEMENT uses a FORGIVING line (Dylan's video: "there's a barrier
 *  right here... there's no trees or anything telling me"). The visuals
 *  (brush lip, dark floor, hem trees) own clearance 0..-16; the player can
 *  wade INTO that scrub before the wall says no. Collision on clean-looking
 *  ground is impossible by construction, and the 16wu margin also swallows
 *  the noise-wobble bumps that caught diagonal walkers. */
export function isForestWalkBlockedAt(x: number, y: number, seed: number): boolean {
  return forestClearance(x, y, seed) <= -16;
}
